/**
 * Notepad building blocks:
 * - RichToolbar / RichTextEditor — a light rich-text editor (contentEditable)
 *   with bold/italic/underline/strike, H1/H2, lists, checkboxes, highlight.
 * - NoteDocEditor — owns a note's title + content with debounced autosave
 *   (flushes on unmount so nothing is lost when panels swap).
 */
import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Bold, Italic, Underline, Strikethrough, Heading1, Heading2, Pilcrow,
  List, ListOrdered, CheckSquare, Highlighter, RemoveFormatting,
} from "lucide-react";

const HIGHLIGHT = "#f7e7a1";

export function RichToolbar({ compact }) {
  const exec = (cmd, val = null) => document.execCommand(cmd, false, val);
  const size = compact ? 13 : 15;
  const Btn = ({ title, testid, onClick, icon: Icon }) => (
    <button
      type="button"
      title={title}
      data-testid={testid}
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      className="p-1.5 rounded hover:bg-[var(--warm-accent)] text-[var(--ink-secondary)] hover:text-[var(--ink-primary)]"
    >
      <Icon size={size} strokeWidth={1.75} />
    </button>
  );
  return (
    <div className="flex items-center flex-wrap gap-0.5" data-testid="notepad-toolbar">
      <Btn title="Bold (⌘B)" testid="np-bold" onClick={() => exec("bold")} icon={Bold} />
      <Btn title="Italic (⌘I)" testid="np-italic" onClick={() => exec("italic")} icon={Italic} />
      <Btn title="Underline (⌘U)" testid="np-underline" onClick={() => exec("underline")} icon={Underline} />
      <Btn title="Strikethrough" testid="np-strike" onClick={() => exec("strikeThrough")} icon={Strikethrough} />
      <span className="w-px h-4 bg-[var(--line)] mx-1" />
      <Btn title="Heading 1" testid="np-h1" onClick={() => exec("formatBlock", "H1")} icon={Heading1} />
      <Btn title="Heading 2" testid="np-h2" onClick={() => exec("formatBlock", "H2")} icon={Heading2} />
      <Btn title="Normal text" testid="np-p" onClick={() => exec("formatBlock", "P")} icon={Pilcrow} />
      <span className="w-px h-4 bg-[var(--line)] mx-1" />
      <Btn title="Bullet list" testid="np-ul" onClick={() => exec("insertUnorderedList")} icon={List} />
      <Btn title="Numbered list" testid="np-ol" onClick={() => exec("insertOrderedList")} icon={ListOrdered} />
      <Btn title="Checkbox" testid="np-check" onClick={() => exec("insertHTML", '<input type="checkbox">&nbsp;')} icon={CheckSquare} />
      <span className="w-px h-4 bg-[var(--line)] mx-1" />
      <Btn title="Highlight" testid="np-highlight" onClick={() => exec("hiliteColor", HIGHLIGHT)} icon={Highlighter} />
      <Btn title="Clear formatting" testid="np-clear" onClick={() => exec("removeFormat")} icon={RemoveFormatting} />
    </div>
  );
}

export function RichTextEditor({ initialHtml, onInput, readOnly, className, placeholder, testId }) {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current) ref.current.innerHTML = initialHtml || "";
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleClick = (e) => {
    if (e.target?.type === "checkbox") {
      if (readOnly) { e.preventDefault(); return; }
      if (e.target.checked) e.target.setAttribute("checked", "");
      else e.target.removeAttribute("checked");
      onInput?.(ref.current.innerHTML);
    }
  };

  return (
    <div
      ref={ref}
      contentEditable={!readOnly}
      suppressContentEditableWarning
      onInput={() => onInput?.(ref.current.innerHTML)}
      onClick={handleClick}
      data-placeholder={placeholder || "Write your notes…"}
      className={`np-doc outline-none text-sm leading-relaxed text-[var(--ink-primary)] ${className || ""}`}
      data-testid={testId || "notepad-editor"}
    />
  );
}

export function NoteDocEditor({ scriptId, note, canEdit, compact = false, page = false, onSaved, leftSlot, rightSlot }) {
  const [title, setTitle] = useState(note.title || "");
  const [status, setStatus] = useState("saved");
  const stateRef = useRef({ title: note.title || "", content: note.content || "", status: "saved" });
  const timer = useRef(null);

  const doSave = async () => {
    const s = stateRef.current;
    s.status = "saving";
    setStatus("saving");
    try {
      const { data } = await api.put(`/scripts/${scriptId}/notepad/${note.id}`, { title: s.title, content: s.content });
      s.status = "saved";
      setStatus("saved");
      onSaved?.(data.note);
    } catch {
      s.status = "dirty";
      setStatus("dirty");
      toast.error("Couldn't save the note");
    }
  };

  const queue = () => {
    stateRef.current.status = "dirty";
    setStatus("dirty");
    clearTimeout(timer.current);
    timer.current = setTimeout(doSave, 800);
  };

  useEffect(() => () => {
    clearTimeout(timer.current);
    const s = stateRef.current;
    if (s.status !== "saved") {
      api.put(`/scripts/${scriptId}/notepad/${note.id}`, { title: s.title, content: s.content }).catch(() => {});
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const statusLabel = status === "saving" ? "Saving…" : status === "dirty" ? "Unsaved" : "Saved";

  const header = (
    <div className={`flex items-center gap-2 shrink-0 border-b border-[var(--line)] ${page ? "px-6 py-3" : "px-3 py-2"}`}>
      {leftSlot}
      <input
        value={title}
        disabled={!canEdit}
        onChange={(e) => { setTitle(e.target.value); stateRef.current.title = e.target.value; queue(); }}
        placeholder="Note title"
        className={`flex-1 min-w-0 bg-transparent outline-none font-display text-[var(--ink-primary)] ${page ? "text-xl" : "text-sm"} disabled:opacity-80`}
        data-testid="note-title-input"
      />
      <span className={`text-[10px] shrink-0 ${status === "saved" ? "text-[var(--ink-muted)]" : "text-[var(--brand)]"}`} data-testid="note-save-status">
        {canEdit ? statusLabel : "Read only"}
      </span>
      {rightSlot}
    </div>
  );

  if (page) {
    return (
      <div data-testid="note-doc-page">
        {header}
        {canEdit && (
          <div className="sticky top-0 z-10 bg-white/95 backdrop-blur-sm border-b border-[var(--line)] px-4 py-1">
            <RichToolbar />
          </div>
        )}
        <RichTextEditor
          initialHtml={note.content}
          readOnly={!canEdit}
          onInput={(html) => { stateRef.current.content = html; queue(); }}
          className="px-10 sm:px-14 py-8 min-h-[8.5in]"
          testId="notepad-editor-full"
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full min-h-0" data-testid="note-doc-panel">
      {header}
      {canEdit && (
        <div className="px-2 py-1 border-b border-[var(--line)] bg-[var(--paper)] shrink-0">
          <RichToolbar compact={compact} />
        </div>
      )}
      <div className="flex-1 min-h-0 overflow-y-auto bg-white">
        <RichTextEditor
          initialHtml={note.content}
          readOnly={!canEdit}
          onInput={(html) => { stateRef.current.content = html; queue(); }}
          className="px-4 py-3 min-h-full"
        />
      </div>
    </div>
  );
}
